import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Field } from "./pickers";
import { StatusPill } from "../lux";
import { STATUS_OPTIONS, TAX_RATE } from "../../pages/bookingFields";
import { PencilSimple, Info } from "@phosphor-icons/react";

const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dt = (s) => (s ? new Date(s).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

const Line = ({ label, value }) => {
  if (value === undefined || value === null || value === "" || value === 0) return null;
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-border/40 last:border-b-0">
      <span className="label-mono shrink-0">{label}</span>
      <span className="text-sm text-right break-words">{value}</span>
    </div>
  );
};

const Block = ({ title, onEdit, children, testid }) => (
  <div className="border border-border bg-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between gap-3 mb-3">
      <h3 className="font-heading text-lg">{title}</h3>
      <button type="button" onClick={onEdit} className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline" data-testid={`${testid}-edit`}>
        <PencilSimple size={12} /> Edit
      </button>
    </div>
    <div>{children}</div>
  </div>
);

export default function StepReview({ formType, form, setForm, onStep, ctx }) {
  const { moneyVisible, vehicles, flightNo } = ctx;
  const v = vehicles.find((x) => x.id === form.vehicle_id);
  const subtotal = Number(form.total) || 0;
  const tax = form.tax_applied ? subtotal * TAX_RATE : 0;

  // SALES-022: an internal hold reviews only what it captured — vehicle, dates,
  // who and why. No money block, no status selector, no approval-queue note.
  if (formType === "internal") {
    return (
      <div className="space-y-5" data-testid="review-internal">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Block title="Internal hold" onEdit={() => onStep(0)} testid="review-use">
            <Line label="Type" value="Internal" />
            <Line label="Used by" value={form.internal_user} />
            <Line label="Reason" value={form.internal_reason} />
          </Block>
          <Block title="Schedule & vehicle" onEdit={() => onStep(1)} testid="review-schedule">
            <Line label="Start" value={dt(form.start_datetime)} />
            <Line label="End" value={dt(form.end_datetime)} />
            <Line label="Vehicle" value={v ? `${v.make} ${v.model}${v.license_plate ? ` · ${v.license_plate}` : ""}` : form.vehicle_label} />
          </Block>
        </div>
        <div className="flex items-start gap-3 rounded-2xl border border-slate-500/30 bg-slate-500/[0.06] px-5 py-4">
          <Info size={17} className="text-slate-300 mt-0.5 shrink-0" />
          <div className="text-sm text-muted-foreground">
            On submit this car is <b className="text-foreground">blocked</b> on the calendar for these dates. It is not a sale — it counts toward no revenue, utilization or salesperson figures.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Block title="Client" onEdit={() => onStep(0)} testid="review-client">
          <Line label="Type" value={<span className="capitalize">{formType}</span>} />
          <Line label="Name" value={form.customer_name} />
          <Line label="Phone" value={form.customer_phone} />
          <Line label="Email" value={form.customer_email} />
          <Line label="Age" value={form.client_age} />
          <Line label="Credit card" value={form.credit_card} />
          <Line label="Insurance" value={form.insurance_coverage ? (form.insurance_coverage === "yes" ? "Yes" : "No") : ""} />
          <Line label="Salesperson" value={form.salesperson} />
          <Line label="Affiliate" value={form.affiliate_name} />
        </Block>

        <Block title="Schedule & vehicle" onEdit={() => onStep(1)} testid="review-schedule">
          <Line label="Start" value={dt(form.start_datetime)} />
          <Line label="End" value={dt(form.end_datetime)} />
          <Line label="Vehicle" value={v ? `${v.make} ${v.model}${v.license_plate ? ` · ${v.license_plate}` : ""}` : form.vehicle_label} />
          <Line label="Driver" value={form.driver_name} />
        </Block>

        <Block title="Details" onEdit={() => onStep(2)} testid="review-details">
          <Line label="Category" value={form.booking_category ? <span className="capitalize">{form.booking_category}</span> : ""} />
          <Line label="Service" value={form.service_type_detail ? <span className="capitalize">{String(form.service_type_detail).replace(/_/g, " ")}</span> : ""} />
          <Line label="Tour" value={form.tour_name} />
          <Line label="Passengers" value={form.passenger_count} />
          <Line label="Pickup" value={form.start_location} />
          <Line label="Drop-off" value={form.end_location} />
          <Line label="Flight" value={flightNo} />
          <Line label="Stops" value={form.in_between_stops} />
          <Line label="Gas filled" value={form.gas_filled} />
          <Line label="Notes" value={form.notes} />
        </Block>

        <Block title="Money" onEdit={() => onStep(2)} testid="review-money">
          <Line label={formType === "rental" ? "Rate" : "Hourly rate"} value={fmt(formType === "rental" ? form.rental_rate : form.hourly_rate)} />
          {formType !== "rental" && <Line label="Hours" value={form.service_hours} />}
          <Line label="Delivery" value={Number(form.delivery) > 0 ? fmt(form.delivery) : ""} />
          <Line label="Other charges" value={Number(form.other_charges) > 0 ? fmt(form.other_charges) : ""} />
          <Line label="Deposit" value={formType === "rental" ? (form.deposit_amount ? fmt(form.deposit_amount) : "") : (Number(form.depo_sent) > 0 ? fmt(form.depo_sent) : "")} />
          {form.tax_applied && <Line label={`HST ${Math.round(TAX_RATE * 100)}%`} value={fmt(tax)} />}
          <div className="flex items-baseline justify-between gap-4 pt-3 mt-1">
            <span className="label-mono">Total</span>
            <span className="font-heading text-2xl money" data-testid="review-total">{fmt(subtotal + tax)}</span>
          </div>
        </Block>
      </div>

      {moneyVisible && (
        <Field label="Create as" hint="Pre-bookings enter the Submitted queue for approval. Choose another status to skip the queue.">
          <Select value={form.status || "pre_booking"} onValueChange={(s) => setForm({ ...form, status: s })}>
            <SelectTrigger className="sm:max-w-xs" data-testid="bookings-field-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      )}

      <div className="flex items-start gap-3 rounded-2xl border border-border bg-white/[0.02] px-5 py-4">
        <Info size={17} className="text-primary mt-0.5 shrink-0" />
        <div className="text-sm text-muted-foreground">
          On submit this booking lands in <b className="text-foreground">Submitted Bookings</b> with the status{" "}
          <StatusPill status="submitted" className="mx-0.5" /> and waits for a manager to approve it. You'll see it in My Bookings straight away.
        </div>
      </div>
    </div>
  );
}
